import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Container from "@/components/base/Container";
import ArticleCard from "@/components/base/ArticleCard";
import Reveal from "@/components/feature/Reveal";
import PageHeader from "@/components/feature/PageHeader";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import HeatRanking from "@/pages/topics/components/HeatRanking";
import { articles, topics } from "@/mocks/melons";

type SortKey = "latest" | "hot" | "quick";

export default function TopicsPage() {
  const [params, setParams] = useSearchParams();
  const activeTopic = params.get("topic") ?? "all";
  const keyword = params.get("q") ?? "";
  const sort = (params.get("sort") as SortKey) ?? "latest";

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    const list = articles.filter((article) => {
      const matchTopic = activeTopic === "all" || article.topicSlug === activeTopic;
      const matchKey =
        !key
        || article.title.toLowerCase().includes(key)
        || article.excerpt.toLowerCase().includes(key)
        || article.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchTopic && matchKey;
    });

    if (sort === "hot") {
      return [...list].sort((a, b) => b.heat - a.heat);
    }
    if (sort === "quick") {
      return [...list].sort((a, b) => Number.parseInt(a.readTime, 10) - Number.parseInt(b.readTime, 10));
    }
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeTopic, keyword, sort]);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://shenguashe.example.com";

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "瓜田 · 全部分类事件复盘",
    description: "按娱乐事件、影视宣发、综艺幕后、品牌代言、音乐现场与网络热点六个分类整理的深度复盘列表。",
    url: `${origin}${__BASE_PATH__}topics`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filtered.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${origin}${__BASE_PATH__}article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}${__BASE_PATH__}` },
      { "@type": "ListItem", position: 2, name: "瓜田", item: `${origin}${__BASE_PATH__}topics` },
    ],
  };

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "latest", label: "最新整理" },
    { key: "hot", label: "热度优先" },
    { key: "quick", label: "快速浏览" },
  ];

  return (
    <>
      <Seo
        title="瓜田｜热门大瓜分类深扒与完整时间线 - 深瓜社"
        description="深瓜社瓜田频道，按娱乐事件、影视宣发、综艺幕后、品牌代言、音乐现场与网络热点分类，收录热门大瓜深扒、未删减原稿与完整时间线，支持分类与关键词筛选。"
        keywords="热门大瓜,大瓜深扒,完整时间线,未删减版,深度解析,瓜田分类"
        path="/topics"
        type="website"
        jsonLd={[collectionSchema, breadcrumbSchema]}
      />
      <SiteHeader />
      <main>
        <PageHeader
          crumbs={[{ label: "瓜田" }]}
          eyebrow="瓜田 · 事件库"
          title="把每一件事，排成一条看得懂的线"
          description="这里收录深瓜社已整理的全部事件复盘。可以按分类浏览，也可以直接搜索关键词，每一篇都标注了时间跨度与仍然待确认的区间。"
        >
          <div className="w-full max-w-sm lg:w-[320px]">
            <label className="text-xs text-foreground-500" htmlFor="topic-search">
              在瓜田里搜索
            </label>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                id="topic-search"
                value={keyword}
                onChange={(event) => updateParam("q", event.target.value)}
                placeholder="试试「时间线」或「未删减」"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword ? (
                <button
                  type="button"
                  onClick={() => updateParam("q", "")}
                  className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-800"
                  aria-label="清除关键词"
                >
                  <i className="ri-close-line"></i>
                </button>
              ) : null}
            </div>
          </div>
        </PageHeader>

        <Container className="py-10 md:py-14">
          <div className="flex flex-col gap-5 border-b border-background-200 pb-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParam("topic", "all")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeTopic === "all"
                    ? "bg-foreground-900 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                全部分类
              </button>
              {topics.map((topic) => (
                <button
                  key={topic.slug}
                  type="button"
                  onClick={() => updateParam("topic", topic.slug)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    activeTopic === topic.slug
                      ? "bg-foreground-900 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {topic.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="whitespace-nowrap text-xs text-foreground-500">排序</span>
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => updateParam("sort", option.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                      sort === option.key
                        ? "bg-secondary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900">{filtered.length}</strong> 篇整理稿
            {activeTopic !== "all" ? (
              <> · 分类：<strong className="text-foreground-900">{topics.find((t) => t.slug === activeTopic)?.name}</strong></>
            ) : null}
            {keyword ? <> · 关键词：<strong className="text-foreground-900">{keyword}</strong></> : null}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              {filtered.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-xl text-foreground-500">
                    <i className="ri-plant-line"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    这片瓜田暂时是空的
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    换个分类或关键词试试，也可以直接看看热度最高的几篇。
                  </p>
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置全部筛选
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {filtered.map((article, index) => (
                    <Reveal key={article.slug} delay={(index % 2) * 80}>
                      <ArticleCard article={article} />
                    </Reveal>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-24">
                <HeatRanking articles={articles} />
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">按分类继续翻</h2>
                  <ul className="mt-4 space-y-2">
                    {topics.map((topic) => (
                      <li key={topic.slug}>
                        <button
                          type="button"
                          onClick={() => updateParam("topic", topic.slug)}
                          className="flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-left text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
                        >
                          <span className="flex items-center gap-2">
                            <i className={topic.icon}></i>
                            {topic.name}
                          </span>
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}